// components/UniversityAutocomplete.jsx
import React, { useEffect, useRef, useState } from "react";
import { getUniversities } from "../APIs/dropdownApi";

export default function UniversityAutocomplete({
  value,
  onChange,
  token, // if you need auth headers inside getUniversities, wire there
  placeholder = "Start typing a university…",
  searchPageSize = 20,
}) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const rootRef = useRef(null);
  const acRef = useRef(null);

  // Close popover on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Debounced search
  useEffect(() => {
    if (acRef.current) acRef.current.abort();
    const ctrl = new AbortController();
    acRef.current = ctrl;

    const t = setTimeout(async () => {
      const q = (query || "").trim();
      if (!q) {
        setOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getUniversities(0, searchPageSize, q, {
          signal: ctrl.signal,
        }); // your API supports search
        setOptions(res || []);
      } catch (_) {
        /* ignore for UX */
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query, searchPageSize]);

  const handleSelect = (name) => {
    onChange?.(name);
    setQuery(name);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <input
        className="w-full border rounded p-2"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded border bg-white shadow">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
          ) : (
            <ul>
              {options.map((u) => (
                <li
                  key={u.id}
                  className="cursor-pointer px-3 py-2 hover:bg-gray-50"
                  onClick={() => handleSelect(u.name)}
                >
                  <div className="text-sm font-medium">{u.name}</div>
                  <div className="text-xs text-gray-500">
                    {[u.city, u.state, u.country].filter(Boolean).join(", ")}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
